// Extracted from App.tsx (ocn-S184-P0). Behavior-preserving move.
import React, { useState, useMemo, useEffect, useRef } from "react";
import { ArrowRight, GitFork, RefreshCw, Rocket, Search, X } from "lucide-react";
import { buildProtectedStreamUrl } from "../../api";
import api, { getSubscriberRepos } from "../../api";
import { openSyncRunStream } from "../../lib/sync-stream";
import type { ProjectSummary, GitHubRepo } from "../../api";
import { useApi } from "../../useApi";
import { RecoveryCard } from "../SubscriberChat";

export function AddProjectPage({ go, registeredProjects, onProjectAdded }: {
  go: (p: string) => void;
  registeredProjects: ProjectSummary[];
  onProjectAdded?: () => void;
}) {
  const [search, setSearch] = useState("");
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [projectId, setProjectId] = useState("");
  const [dispatching, setDispatching] = useState(false);
  const [dispatchMsg, setDispatchMsg] = useState<string | null>(null);
  const [dispatchStage, setDispatchStage] = useState<string>('');
  const [dispatchElapsed, setDispatchElapsed] = useState(0);
  const dispatchTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [onboardingSyncId, setOnboardingSyncId] = useState<string | null>(null);
  const [onboardingPhases, setOnboardingPhases] = useState<import('../../api').SyncPhase[]>([]);
  const [onboardingCurrentPhase, setOnboardingCurrentPhase] = useState<string>('');
  const syncEsRef = useRef<{ close: () => void } | null>(null);
  const [showCreateRepo, setShowCreateRepo] = useState(false);
  const [newRepoName, setNewRepoName] = useState("");
  const [newRepoDesc, setNewRepoDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: repos, loading: reposLoading, error: reposError, refetch: refetchRepos } = useApi(
    // Punch 95735b3a: the lumeniqjs org listing is service-only (403 for
    // subscribers). Read only the caller tenant's own installation.
    () => getSubscriberRepos().then((res): GitHubRepo[] => (Array.isArray(res?.repos) ? res.repos : []).map((fullName: string) => ({
      full_name: fullName,
      name: fullName.includes('/') ? fullName.slice(fullName.indexOf('/') + 1) : fullName,
      description: null,
      language: null,
    }) as GitHubRepo)), []
  );

  useEffect(() => { refetchRepos(); }, [refetchRepos]);

  const registeredRepoNames = useMemo(() => {
    const names = new Set<string>();
    registeredProjects.forEach((p: ProjectSummary) => {
      names.add(p.project_id.toLowerCase());
      if (p.repo) names.add(p.repo.split('/').pop()?.toLowerCase() || '');
    });
    return names;
  }, [registeredProjects]);

  const filteredRepos = useMemo(() => {
    if (!repos) return [];
    const q = search.toLowerCase().trim();
    const filtered = q
      ? repos.filter((r: GitHubRepo) => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q))
      : repos;
    return [...filtered].sort((a: GitHubRepo, b: GitHubRepo) => {
      const aReg = registeredRepoNames.has(a.name.toLowerCase()) ? 1 : 0;
      const bReg = registeredRepoNames.has(b.name.toLowerCase()) ? 1 : 0;
      if (aReg !== bReg) return aReg - bReg;
      return a.name.localeCompare(b.name);
    });
  }, [repos, search, registeredRepoNames]);

  const selectRepo = (r: GitHubRepo) => {
    if (registeredRepoNames.has(r.name.toLowerCase())) return;
    setSelectedRepo(r);
    setProjectId(r.name);
  };

  useEffect(() => {
    return () => {
      if (dispatchTimerRef.current) clearInterval(dispatchTimerRef.current);
      if (syncEsRef.current) syncEsRef.current.close();
    };
  }, []);

  const connectOnboardingSSE = (runId: string, pid: string, streamToken?: string | null) => {
    syncEsRef.current?.close();
    const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';
    const url = buildProtectedStreamUrl(API_BASE, `/api/sync/stream/${runId}`, streamToken);
    if (!url) { setDispatchStage('Live updates unavailable'); setDispatchMsg('Sync continues in the background. Open the project Sync page to view status.'); setDispatching(false); return; }
    const handle = openSyncRunStream({
      url,
      fetchStatus: () => api.syncStatus(pid),
      statusOf: value => value.last_run?.status,
      onEvent: event => {
        try {
          const data = JSON.parse(event.data);
          if (data.phases) setOnboardingPhases(data.phases);
          if (data.current_phase) { setOnboardingCurrentPhase(data.current_phase); const phase = (data.phases || []).find((p: { id: string; name?: string }) => p.id === data.current_phase); if (phase) setDispatchStage((phase.name || data.current_phase) + '...'); }
          if (data.status === 'review_required') { setDispatchStage('Review required'); setDispatchMsg('Doc review needed — redirecting to project...'); onProjectAdded?.(); setTimeout(() => go(`project:${pid}`), 2000); }
        } catch { /* ignore malformed stream events */ }
      },
      onStatus: value => { if (value.last_run?.phases) setOnboardingPhases(value.last_run.phases); },
      onTerminal: status => {
        if (dispatchTimerRef.current) { clearInterval(dispatchTimerRef.current); dispatchTimerRef.current = null; }
        syncEsRef.current = null;
        if (status === 'completed') { setDispatchStage('Sync complete!'); setDispatchMsg('Project bootstrapped! Redirecting...'); onProjectAdded?.(); setTimeout(() => go(`project:${pid}`), 1500); }
        else { setDispatchMsg(status === 'failed' ? 'Sync failed — check the Sync page for details' : 'Sync cancelled'); setDispatching(false); }
      },
    });
    syncEsRef.current = handle;
  };

  const startOnboard = async () => {
    if (!selectedRepo) return;
    const pid = projectId || selectedRepo.name;
    setDispatching(true);
    setDispatchMsg(null);
    setDispatchStage('Authenticating...');
    setDispatchElapsed(0);
    setOnboardingPhases([]);
    setOnboardingCurrentPhase('');
    setOnboardingSyncId(null);
    const startTime = Date.now();
    dispatchTimerRef.current = setInterval(() => { setDispatchElapsed(Math.floor((Date.now() - startTime) / 1000)); }, 1000);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30_000);
    try {
      setDispatchStage('Checking session...');
      const [repoOwner] = selectedRepo.full_name.split('/');
      setDispatchStage('Registering project...');
      const result = await api.dispatch(pid, `Onboard project ${selectedRepo.full_name}`, undefined, repoOwner, selectedRepo.name);
      if (result.status === 'onboarding' && result.sync_run_id) {
        setOnboardingSyncId(result.sync_run_id);
        if (result.phases) setOnboardingPhases(result.phases);
        setDispatchStage('Repo scan...');
        connectOnboardingSSE(result.sync_run_id, pid, (result as { stream_token?: string }).stream_token ?? null);
      } else {
        setDispatchStage('Pipeline started!');
        setDispatchMsg('Pipeline started! Redirecting...');
        onProjectAdded?.();
        setTimeout(() => go(`project:${pid}`), 1500);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start onboarding';
      if (controller.signal.aborted) {
        setDispatchMsg('Request timed out after 30s \u2014 please check your connection and try again');
      } else if (msg.includes('Session expired')) {
        setDispatchMsg('Session expired \u2014 please sign in again');
      } else {
        setDispatchMsg(msg);
      }
      setDispatchStage('');
      setDispatching(false);
    } finally {
      clearTimeout(timeout);
    }
  };

  const langColor: Record<string, string> = {
    TypeScript: '#3178c6', JavaScript: '#f1e05a', Python: '#3572A5', Go: '#00ADD8',
    Rust: '#dea584', Java: '#b07219', Ruby: '#701516', Shell: '#89e051', CSS: '#563d7c',
  };

  return (
    <div>
      <div className="page-title-row">
        <div>
          <h2 className="page-title">Add Project</h2>
          <p className="page-subtitle">Connect an existing repo or bootstrap a new project from scratch.</p>
        </div>
        <button className="btn btn-sm" onClick={refetchRepos} disabled={reposLoading} title="Refresh repo list">
          <RefreshCw size={12} className={reposLoading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Bootstrap New Project */}
      <div onClick={() => go('wizard')} className="card" style={{ maxWidth: 640, marginBottom: 'var(--space-4)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--space-3)', border: '1px dashed var(--orange)', background: 'var(--orange-dim)', padding: '12px var(--space-4)' }}
        onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.borderStyle = 'solid'; }}
        onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.borderStyle = 'dashed'; }}>
        <Rocket size={16} style={{ color: 'var(--orange)', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--orange)' }}>Bootstrap New Project</div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2 }}>Create a project from scratch &mdash; set up GitHub, Supabase, Railway, Netlify, DNS, and more in a guided 10-step wizard.</div>
        </div>
        <ArrowRight size={14} style={{ color: 'var(--orange)', flexShrink: 0 }} />
      </div>

      {/* Search */}
      <div style={{ position: 'relative', maxWidth: 640, marginBottom: 'var(--space-4)' }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-ghost)' }} />
        <input className="input input-mono" style={{ paddingLeft: 34 }} placeholder="Search repositories..." value={search} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} />
      </div>

      {/* Repo list (simplified - key elements preserved) */}
      <div style={{ maxWidth: 640, maxHeight: 400, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', background: 'var(--surface)' }}>
        {reposLoading && <div style={{ padding: 'var(--space-5)', textAlign: 'center' }}><div className="spinner" style={{ width: 16, height: 16, margin: '0 auto 8px' }} /><span className="muted" style={{ fontSize: 'var(--text-sm)' }}>Loading repositories...</span></div>}
        {/* ocn-S818-P0 Part 6: never a bare error string -- REUSE the
            ocn-S809 RecoveryCard rather than writing a second one. */}
        {reposError && (
          <div style={{ padding: 'var(--space-4)' }}>
            <RecoveryCard
              reasonCode={null}
              onRetry={refetchRepos}
              onBackToDashboard={() => go('overview')}
              onStartNewChat={() => go('subscriber-chat:app_wizard')}
            />
          </div>
        )}
        {filteredRepos.map((r: GitHubRepo) => {
          const isRegistered = registeredRepoNames.has(r.name.toLowerCase());
          const isSelected = selectedRepo?.name === r.name;
          return (
            <div key={r.name} onClick={() => selectRepo(r)} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: '10px var(--space-4)', borderBottom: '1px solid var(--border)', cursor: isRegistered ? 'not-allowed' : 'pointer', opacity: isRegistered ? 0.4 : 1, background: isSelected ? 'var(--orange-dim)' : 'transparent', borderLeft: isSelected ? '3px solid var(--orange)' : '3px solid transparent' }}>
              <GitFork size={14} style={{ color: isRegistered ? 'var(--text-ghost)' : 'var(--text-muted)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span className="mono" style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: isRegistered ? 'var(--text-ghost)' : isSelected ? 'var(--orange)' : 'var(--text)' }}>{r.name}</span>
                {r.description && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</div>}
              </div>
              {r.language && <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: langColor[r.language] || 'var(--text-ghost)' }} />{r.language}</span>}
            </div>
          );
        })}
      </div>

      {/* Onboarding modal */}
      {selectedRepo && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={(e: React.MouseEvent) => { if (e.target === e.currentTarget && !dispatching) { setSelectedRepo(null); setDispatchMsg(null); } }}>
          <div className="card" style={{ width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', padding: 'var(--space-5)', position: 'relative' }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            {!dispatching && <button onClick={() => { setSelectedRepo(null); setDispatchMsg(null); }} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={16} /></button>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}><Rocket size={16} style={{ color: 'var(--orange)' }} /><span style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--text)' }}>Start Onboarding?</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                <GitFork size={14} style={{ color: 'var(--orange)', flexShrink: 0 }} />
                <span className="mono" style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--orange)' }}>{selectedRepo.full_name}</span>
              </div>
              <div><label className="section-label">Project ID</label><input className="input input-mono" value={projectId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectId(e.target.value)} placeholder={selectedRepo.name} /></div>

              {dispatching && (
                <div className="card" style={{ padding: '12px 16px', background: 'var(--blue-dim)', borderColor: 'var(--blue)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div className="spinner" style={{ width: 14, height: 14 }} />
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--blue)' }}>{dispatchStage || 'Starting...'}</span>
                    <span className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginLeft: 'auto' }}>{dispatchElapsed}s</span>
                  </div>
                  {onboardingPhases.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 8 }}>
                      {onboardingPhases.map((p: import('../../api').SyncPhase) => (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-xs)', color: p.status === 'completed' ? 'var(--green)' : p.status === 'running' ? 'var(--blue)' : 'var(--text-muted)' }}>
                          <span style={{ width: 16, textAlign: 'center', flexShrink: 0 }}>{p.status === 'completed' ? '\u2705' : p.status === 'running' ? '\u25B6' : p.status === 'failed' ? '\u274C' : '\u25CB'}</span>
                          <span>{p.name}</span>
                          {p.status === 'running' && <div className="spinner" style={{ width: 10, height: 10, marginLeft: 'auto' }} />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {dispatchMsg && !dispatching && (
                <div className="card" style={{ padding: '8px 12px', background: dispatchMsg.includes('bootstrapped') || dispatchMsg.includes('Pipeline') ? 'var(--green-dim)' : 'var(--red-dim)', borderColor: dispatchMsg.includes('bootstrapped') || dispatchMsg.includes('Pipeline') ? 'var(--green)' : 'var(--red)', fontSize: 'var(--text-sm)', color: dispatchMsg.includes('bootstrapped') || dispatchMsg.includes('Pipeline') ? 'var(--green)' : 'var(--red)' }}>
                  {dispatchMsg}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={dispatching} onClick={startOnboard}>
                  {dispatching ? <div className="spinner" style={{ width: 13, height: 13 }} /> : <Rocket size={13} />}
                  {dispatching ? dispatchStage || 'Starting...' : 'Start Onboarding'}
                </button>
                {!dispatching && <button className="btn" onClick={() => { setSelectedRepo(null); setDispatchMsg(null); }}>Cancel</button>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
